import { inject, Injectable, Injector } from '@angular/core';
import { toObservable } from '@angular/core/rxjs-interop';
import { ActivatedRouteSnapshot, CanActivate, Router, UrlTree } from '@angular/router';
import { filter, firstValueFrom } from 'rxjs';

import { RoutePathEnum } from '@connect-services/client/models/routes.model';
import { WarehouseAndFormsAccessPermissionsService } from '@connect-services/client/services/warehouse-and-forms-access-permissions.service';

export enum ManagementDataAccessContextEnum {
    FORMS = 'forms',
    LANDING = 'landing',
    WAREHOUSE = 'warehouse',
}

@Injectable({ providedIn: 'root' })
export class WarehouseAndFormManagementDataAccessGuard implements CanActivate {
    public readonly injector = inject(Injector);
    private readonly router = inject(Router);

    private readonly warehouseAndFormsAccessPermissionsService = inject(WarehouseAndFormsAccessPermissionsService);

    public async canActivate(route: ActivatedRouteSnapshot): Promise<boolean | UrlTree> {
        await firstValueFrom(
            toObservable(this.warehouseAndFormsAccessPermissionsService.initialized, { injector: this.injector }).pipe(
                filter(Boolean),
            ),
        );

        const context = route.data['managementAccess'];

        if (!context) {
            return this.noPermission();
        }

        switch (context) {
            case ManagementDataAccessContextEnum.FORMS:
                return this.canActivateForms();

            case ManagementDataAccessContextEnum.LANDING:
                return this.canActivateLanding();

            case ManagementDataAccessContextEnum.WAREHOUSE:
                return this.canActivateWarehouse();

            default:
                return this.noPermission();
        }
    }

    private canActivateForms(): boolean | UrlTree {
        if (this.warehouseAndFormsAccessPermissionsService.canAccessForms()) {
            return true;
        }

        return this.noPermission();
    }

    private canActivateLanding(): UrlTree {
        if (this.warehouseAndFormsAccessPermissionsService.canAccessWarehouse()) {
            return this.router.createUrlTree([RoutePathEnum.MANAGEMENT_DATA, RoutePathEnum.ORGANIZATION_DATA]);
        }

        if (this.warehouseAndFormsAccessPermissionsService.canAccessForms()) {
            return this.router.createUrlTree([RoutePathEnum.MANAGEMENT_DATA, RoutePathEnum.FORMS]);
        }

        return this.noPermission();
    }

    private canActivateWarehouse(): boolean | UrlTree {
        if (this.warehouseAndFormsAccessPermissionsService.canAccessWarehouse()) {
            return true;
        }

        return this.noPermission();
    }

    private noPermission(): UrlTree {
        return this.router.createUrlTree([RoutePathEnum.MANAGEMENT_DATA, RoutePathEnum.MANAGEMENT_NO_PERMISSION]);
    }
}
